/* eslint-disable react/no-danger */

import {
  BpkAccordionItem,
  withAccordionItemState,
} from '@skyscanner-internal/backpack-web/bpk-component-accordion';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  constants,
  namespace,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';
import observer from 'saddlebag-observer';

import currentLandingPagesOptions from '../../../utils/richTextBrandStyles';
import RichText from '../../RichText';
import { useMBDContext } from '../../common/MBD/useMBDContext';

import type { Document } from '@contentful/rich-text-types';
import type { TrackingContextProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/TrackingContextProps';

import STYLES from './FAQ.module.scss';

const StatefulAccordionItem = withAccordionItemState(BpkAccordionItem);

type FAQAnswer = string | Document;

const isRichText = (answer: FAQAnswer): answer is Document =>
  typeof answer === 'object' && answer !== null && 'nodeType' in answer;

export type FAQProps = {
  id?: string;
  category?: string;
  question: string;
  answer: FAQAnswer;
  expanded?: boolean;
  onClick?: () => void;
  questionIndex?: number;
  currency: string;
  deviceType: string;
  locale: string;
  market: string;
  pageType: string;
};

const getShortId = (id?: string, questionIndex?: number) => {
  if (id) return id;
  if (questionIndex !== undefined) return questionIndex.toString();

  return 'faq';
};

const extendedOnClick = (
  onClickHandler: FAQProps['onClick'],
  faqId: string,
  { currency, deviceType, locale, market, pageType }: TrackingContextProps,
) => {
  if (typeof onClickHandler === 'function') {
    onClickHandler();
  }

  observer.publish('grappler-track', {
    eventName: 'FAQClicked',
    fullSchemaName: 'faq_component.FAQClicked',
    message: {
      common_properties: {
        culture_settings: {
          country: market,
          currency,
          locale,
        },
        page_type: constants[pageType],
        platform: deviceType || constants.UNSET_PLATFORM,
      },
      faq_id: faqId,
    },
    isMiniEvent: true,
    consentRequired: true,
    microsite: {
      name: namespace,
    },
  });

  observer.publish('tracking-event', {
    ga: [
      {
        category: `react-faq`,
        action: 'clicked',
        label: faqId,
      },
    ],
  });
};

const FAQ = ({
  answer,
  category = undefined,
  expanded = false,
  id = undefined,
  onClick = undefined,
  question,
  questionIndex = undefined,
  ...trackingContext
}: FAQProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const questionId = getShortId(id, questionIndex);
  const richTextAnswerToRender = isRichText(answer) ? answer : null;
  const htmlOrPlainTextAnswer = typeof answer === 'string' ? answer : '';

  return (
    <div
      data-tracking-element-id={`${rawComponentName}_item_${questionId}`}
      data-tracking-common-params={buildMBDParam(`${rawComponentName}_item`, {
        faq_id: questionId,
      })}
      className={STYLES.FAQ}
    >
      <StatefulAccordionItem
        initiallyExpanded={expanded}
        id={`${questionId}_question`}
        onClick={() => extendedOnClick(onClick, questionId, trackingContext)}
        tagName="h3"
        textStyle={TEXT_STYLES.heading4}
        title={question}
      >
        {richTextAnswerToRender ? (
          <RichText
            body={richTextAnswerToRender}
            options={currentLandingPagesOptions}
          />
        ) : (
          <BpkText>
            <div dangerouslySetInnerHTML={{ __html: htmlOrPlainTextAnswer }} />
          </BpkText>
        )}
      </StatefulAccordionItem>
    </div>
  );
};

export default FAQ;
